/**
 * #731 — the order card's row BOUNDARY.
 *
 * Scope item 2 asks for extracted row boundaries with "a complete, explicit
 * dependency signature and stable callbacks/props"; item 5 says `memo()` is the
 * last step, "not as the architecture". This is that boundary.
 *
 * ### Why a component and not a cache
 *
 * The first attempt kept the JSX in the parent and memoised each row's handlers
 * in a keyed cache held in a ref. It produced the right numbers and was wrong:
 * reading and mutating a ref DURING RENDER is not sound under concurrent
 * rendering — a render React discards would leave the cache half-rotated — and
 * `react-hooks/refs` rejects it, which the repo's lint budget treats as a hard
 * gate. A component gets the same result with none of that: each row instance
 * owns its own `useCallback`s, so React does the per-row memoisation natively.
 *
 * ### The dependency signature is the safety contract
 *
 * Each handler lists every value it closes over. A captured value missing from
 * its list would let a cancel or an edit run against a stale figure — the one
 * failure worse than the slowness this ticket fixes. The props are also the
 * signature `memo` compares: `vm` arrives already identity-stabilised (see
 * `stableVm.ts`), so an unchanged row compares equal and is not invoked.
 *
 * `highlighted` is deliberately a plain boolean rather than a derived object:
 * the same-game map is rebuilt every publication by design, so passing it (or
 * anything derived from its identity) would rebuild every row for a highlight
 * that did not move. A boolean compares by value.
 */
import { memo, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { OrderCard } from '../../components/cards/OrderCard';
import { cardAnnotationsFor, useCardAnnotationsActive } from '../../components/cards/CardAnnotations';
import type { OrderCardVM } from '../../components/cards/orderCardModel';
import type { EventStateCertainty } from '../../lib/sport/eventStateCertainty';
import type { EditOrderData, SameGameRowRef } from './index';
import { NO_US_DISPATCH, usOrderActionProps, usPinOriginOf, type UsRowDispatch } from './usTrackerRows';

export interface OrderCardRowProps {
  vm: OrderCardVM;
  eventStateCertainty: EventStateCertainty | undefined;
  highlighted: boolean;
  queueCriticalFlash: boolean;
  queueWarningFlash: boolean;
  fellBehindFlash: boolean;
  velocitySpikeSeverity: React.ComponentProps<typeof OrderCard>['velocitySpikeSeverity'];
  velocityMuted: boolean;
  divergence: React.ComponentProps<typeof OrderCard>['divergence'];
  divergenceFlash: boolean;
  upOdds: React.ComponentProps<typeof OrderCard>['upOdds'];
  detailHref: string | null;
  canceling: boolean;
  /** #748 — forwarded to OrderCard through the `...cardProps` rest spread. */
  cancelUnconfirmed?: boolean;
  cancelTitle?: string;
  pinned: boolean;
  /** The row's own label — derived once by the parent and passed, so the two
   *  handlers that show it to the trader cannot disagree with the header. */
  label: string;
  /** Poly navigate fallback for `onOpen`; null for Kalshi rows. */
  href: string | null;
  /** #828 — stable row identity for the reading-position primitive. Reaches
   *  `OrderCard` through the `...cardProps` rest spread. */
  viewAnchor?: string;
  /* Stable parent dispatchers. */
  setHoveredRow: Dispatch<SetStateAction<SameGameRowRef | null>>;
  orderCardOpen: (
    vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side'>,
    href: string | null,
  ) => () => void;
  requestCancelOrder: (target: {
    venue: OrderCardVM['venue'];
    orderId: string;
    label: string;
  }) => void;
  setEditOrder: Dispatch<SetStateAction<EditOrderData | null>>;
  togglePin: (entry: { key: string; kind: 'order'; label: string; us?: { accountId: string; view: 'own' | 'desk' } }) => void;
  toggleVelocityMute: (key: string) => void;
  /** #1226 — a Polymarket US row's handlers (stable); the row resolves its
   *  action set from `vm.us`. */
  usDispatch?: UsRowDispatch;
}

function OrderCardRowInner({
  vm,
  label,
  href,
  setHoveredRow,
  orderCardOpen,
  requestCancelOrder,
  setEditOrder,
  togglePin,
  toggleVelocityMute,
  usDispatch = NO_US_DISPATCH,
  ...cardProps
}: OrderCardRowProps) {
  // #979: fill the annotations slot only on a page that shows annotations.
  const annotated = useCardAnnotationsActive();
  const onMouseEnter = useCallback(
    () => setHoveredRow({ venue: vm.venue, ticker: vm.ticker, key: vm.key }),
    [setHoveredRow, vm.venue, vm.ticker, vm.key],
  );
  const onMouseLeave = useCallback(() => setHoveredRow(null), [setHoveredRow]);
  const onToggleVelocityMute = useCallback(
    () => toggleVelocityMute(vm.key),
    [toggleVelocityMute, vm.key],
  );
  const us = vm.us;
  const onOpen = useCallback(() => {
    if (us) usDispatch.open({ accountId: us.accountId, marketSlug: vm.ticker, outcome: vm.side ?? 'yes' });
    else orderCardOpen(vm, href)();
  }, [orderCardOpen, vm, href, us, usDispatch]);
  const onCancel = useCallback(
    () => requestCancelOrder({ venue: vm.venue, orderId: vm.cancelId, label }),
    [requestCancelOrder, vm.venue, vm.cancelId, label],
  );
  // #1226 — a US row's action set; its cancel/edit go to the account reader.
  const actions = useMemo(() => (us ? usOrderActionProps(us, usDispatch) : undefined), [us, usDispatch]);
  const onEdit = useCallback(() => {
    if (vm.venue === 'polymarket_us') return;
    // vm.title / vm.subtitle combine into the same label the card already shows
    // in its header — keep the modal consistent with what the trader was just
    // looking at. Poly cards have side === null (no YES/NO concept on a token);
    // fall back to 'yes' as a placeholder badge.
    setEditOrder({
      venue: vm.venue,
      orderId: vm.cancelId,
      marketLabel: label,
      side: vm.side ?? 'yes',
      action: vm.isSell ? 'sell' : 'buy',
      remainingCount: vm.remainingQty,
      currentPriceCents: vm.bidCents ?? 50,
      ticker: vm.ticker,
      tickSize: vm.tickSize,
      negRisk: vm.negRisk,
    });
  }, [setEditOrder, vm, label]);
  const onTogglePin = useCallback(
    () => togglePin({ key: vm.key, kind: 'order', label, ...usPinOriginOf(us) }),
    [togglePin, vm.key, label, us],
  );

  return (
    <OrderCard
      vm={vm}
      {...cardProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onToggleVelocityMute={onToggleVelocityMute}
      onOpen={onOpen}
      onCancel={onCancel}
      onEdit={onEdit}
      onTogglePin={onTogglePin}
      actions={actions}
      annotations={
        annotated ? cardAnnotationsFor({ kind: 'order', venue: vm.venue, venueOrderId: vm.cancelId }) : undefined
      }
    />
  );
}

/** The boundary. `memo` is the LAST step here, on top of stable identities. */
export const OrderCardRow = memo(OrderCardRowInner);
